import React, { useEffect, useRef, useState } from "react";
import { Sparkles, Send, Loader2, Maximize2, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

const STORAGE_KEY = "absuk-dhamma-session";

const STARTERS = [
  "What is the core teaching of the Buddha?",
  "Explain the Twenty-Two Vows",
  "How do I begin a daily meditation practice?",
];

export default function DhammaAssistantPanel({ fullPage = false }) {
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  // Load history once if we have a session
  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    api
      .get(`/dhamma/chat/${sessionId}/history`)
      .then((r) => {
        if (alive) setMessages(r.data.messages || []);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setError(null);
    setMessages((m) => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const res = await api.post("/dhamma/chat", { session_id: sessionId, message: text });
      const sid = res.data.session_id;
      if (sid && sid !== sessionId) {
        setSessionId(sid);
        localStorage.setItem(STORAGE_KEY, sid);
      }
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not reach the assistant.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const heightClass = fullPage ? "h-[calc(100vh-12rem)]" : "h-[420px]";

  return (
    <div
      data-testid="dhamma-assistant-panel"
      className={`flex flex-col rounded-xl bg-white border border-navy/10 shadow-sm overflow-hidden ${heightClass}`}
    >
      <header className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-[#6C4EFF]/8 to-[#2D5BFF]/8 border-b border-navy/10">
        <div className="flex items-center gap-2.5">
          <div className="p-1.5 rounded-md bg-gradient-to-br from-[#6C4EFF] to-[#2D5BFF] text-white">
            <Sparkles size={14} />
          </div>
          <div>
            <div className="text-sm font-medium text-navy leading-tight">AI Dhamma Assistant</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-navy/50 leading-tight">
              Beta · Your Dhamma Guide
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button
              onClick={reset}
              data-testid="dhamma-reset"
              className="p-1.5 text-navy/50 hover:text-navy"
              title="Start a new conversation"
            >
              <RotateCcw size={14} />
            </button>
          )}
          {!fullPage && (
            <Link
              to="/platform/assistant"
              data-testid="dhamma-expand"
              className="p-1.5 text-navy/50 hover:text-navy"
              title="Open full conversation"
            >
              <Maximize2 size={14} />
            </Link>
          )}
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-pearl/40">
        {messages.length === 0 && (
          <div className="text-center pt-2" data-no-translate>
            <div className="font-display text-lg text-navy">Namo Buddhāya 🪷</div>
            <p className="text-xs text-navy/55 italic mt-1 mb-4 px-3">
              Ask anything about the Buddha's Dhamma, Babasaheb's writings, or our Sangha.
            </p>
            <div className="space-y-2 px-2">
              {STARTERS.map((p) => (
                <button
                  key={p}
                  onClick={() => send(p)}
                  data-testid={`dhamma-starter-${p.slice(0, 18).replace(/\s+/g, "-").toLowerCase()}`}
                  className="block w-full text-left text-xs px-3 py-2 bg-white border border-navy/15 text-navy/75 hover:border-saffron hover:text-saffron rounded-md transition-colors"
                >
                  → {p}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[88%] px-3.5 py-2 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-navy text-pearl rounded-br-sm"
                  : "bg-white border border-navy/10 text-navy rounded-bl-sm"
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="px-3.5 py-2 rounded-2xl rounded-bl-sm bg-white border border-navy/10 inline-flex items-center gap-2 text-navy/55 text-sm">
              <Loader2 size={14} className="animate-spin" /> Thinking…
            </div>
          </div>
        )}
        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2 text-center">
            {error}
          </div>
        )}
      </div>

      <div className="border-t border-navy/10 bg-white px-3 py-2.5">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about Dhamma, Babasaheb, meditation…"
            rows={1}
            data-testid="dhamma-input"
            disabled={busy}
            className="flex-1 resize-none border border-navy/15 px-3 py-2 rounded-md text-sm text-navy focus:outline-none focus:border-saffron max-h-28 disabled:opacity-50"
          />
          <button
            onClick={() => send()}
            disabled={busy || !input.trim()}
            data-testid="dhamma-send"
            className="p-2.5 bg-navy text-pearl rounded-md hover:bg-navy-light disabled:opacity-30"
            aria-label="Send"
          >
            <Send size={15} />
          </button>
        </div>
        <p className="text-[10px] text-navy/40 text-center mt-1.5 italic">
          AI-generated · for guidance only · powered by Gemini
        </p>
      </div>
    </div>
  );
}
